import {
    Delete as DeleteIcon,
    LocalOffer as TagIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles/makeStyles';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { updateDashboardFunc } from '../../../lib/helper';
import { CustomField as CustomFieldType, CustomFieldType as FieldKind } from "../../../types/common";
import { Dashboard } from '../../../types/files';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '20px',
      maxWidth: '800px',
      width: '90%',
      maxHeight: '85vh',
    }
  },
  dialogTitle: {
    padding: '28px 32px',
    borderBottom: '1px solid #e5e7eb',
  },
  headerStack: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  icon: {
    color: '#64748b',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  },
  dialogContent: {
    padding: '32px',
  },
  sectionLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#64748b',
    marginBottom: '10px',
  },
  hint: {
    fontSize: '13px',
    color: '#94a3b8',
    marginTop: '-6px',
    marginBottom: '12px',
    lineHeight: 1.5,
  },
  fieldNameInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      fontSize: '16px',
      fontWeight: 500,
      color: '#1eb6a8',
      borderRadius: '10px',
      '& fieldset': {
        borderWidth: '2px',
        borderColor: '#e5e7eb',
      },
      '&:hover fieldset': {
        borderColor: '#1eb6a8',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1eb6a8',
      },
    }
  },
  editButton: {
    width: '40px',
    height: '40px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    '&:hover': {
      borderColor: '#1eb6a8',
      backgroundColor: '#f0fdf9',
    }
  },
  textarea: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderWidth: '2px',
        borderColor: '#e5e7eb',
      },
      '&:hover fieldset': {
        borderColor: '#1eb6a8',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1eb6a8',
      },
    }
  },
  addTagInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderWidth: '2px',
        borderColor: '#e5e7eb',
      },
    }
  },
  addButton: {
    padding: '12px 24px',
    borderRadius: '10px',
    border: '2px solid #1eb6a8',
    color: '#1eb6a8',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#1eb6a8',
      color: 'white',
    }
  },
  bulkControls: {
    padding: '8px 12px',
    backgroundColor: '#f8f9fc',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  bulkDeleteButton: {
    padding: '6px 12px',
    backgroundColor: 'white',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#dc2626',
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#fee',
      borderColor: '#dc2626',
    }
  },
  tagsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    minHeight: '40px',
    padding: '12px',
    backgroundColor: 'white',
    borderRadius: '10px',
    border: '2px solid #e5e7eb',
    position: 'relative',
  },
  emptyTags: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#94a3b8',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  tagChip: {
    padding: '8px 16px',
    backgroundColor: '#f1f5f9',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#64748b',
    '& .MuiChip-deleteIcon': {
      color: '#94a3b8',
      fontSize: '20px',
      '&:hover': {
        color: '#64748b',
      }
    }
  },
  copyPasteButton: {
    color: '#1eb6a8',
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    padding: '4px 8px',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: '#f0fdf9',
    }
  },
  importButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#1eb6a8',
    color: 'white',
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    marginTop: '12px',
    '&:hover': {
      backgroundColor: '#1a9d91',
    }
  },
  select: {
    minWidth: '80px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontWeight: 500,
      color: '#1eb6a8',
      '& fieldset': {
        borderWidth: '2px',
        borderColor: '#e5e7eb',
      },
    }
  },


    fieldName: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        textTransform: 'capitalize',
    },
    nameField: {
        minWidth: 'fit-content',
        width: '100%',
        '& .MuiOutlinedInput-root': {
            height: '45px',
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#1eb6a8',
            borderRadius: 12,
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused': {
                background: '#f0fdf9 !important',
            },
            '&.Mui-error fieldset': {
                borderColor: '#dc2626 !important',
            },
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: '#dc2626',
            fontFamily: 'Inter',
            fontSize: '0.8rem',
            marginLeft: 0,
        },
    },
    descField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            fontFamily: 'Lato',
            fontSize: '14px',
            color: '#1e293b',
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused': {
                background: '#f0fdf9 !important',
            },
        },
    },

    dialogActions: {
        padding: '20px 32px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    deleteFieldButton: {
        height: '40px',
        color: '#dc2626',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '8px',
        padding: '10px 16px',
        '&:hover': {
            backgroundColor: '#fee',
        },
    },
    backButton: {
        height: '40px',
        padding: '10px 24px',
        border: '2px solid #e5e7eb',
        borderRadius: '10px',
        backgroundColor: 'white',
        color: '#64748b',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            borderColor: '#cbd5e1',
            backgroundColor: 'white',
        },
    },
    saveButton: {
        height: '40px',
        padding: '10px 24px',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&.Mui-disabled': {
            backgroundColor: '#e5e7eb',
            color: '#94a3b8',
        },
    },
    saveButtonActive: {
        backgroundColor: '#1eb6a8',
        color: 'white',
        '&:hover': {
            backgroundColor: '#1a9d91',
        },
    },
}));

const ManageCustomFieldsNewModal: FC<{
  open: boolean;
  filterType?: string;
  initialName?: string;
  onClose: () => void;
}> = ({ open, filterType, initialName, onClose }) => {
    const classes = useStyles();
    const { settingsLoaded, customFields, setCustomFields, tagGroups } = useContext(GroupSettingsContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);
    const [name, setName] = useState<string>('');
    const [additionalContextQuery, setAdditionalContextQuery] = useState<string>('');

    const isDuplicateName = useMemo(() => {
        const trimmed = name.trim().toLowerCase();
        if (!trimmed) return false;
        // Allow keeping the same name (editing context only)
        if (filterType && trimmed === filterType.trim().toLowerCase()) return false;
        return customFields.some(({ name: n }) => n.trim().toLowerCase() === trimmed)
            || tagGroups.some(({ title }) => title.trim().toLowerCase() === trimmed);
    }, [name, filterType, customFields, tagGroups]);

    const isSaveDisabled = useMemo(() => !name.trim() || isDuplicateName, [name, isDuplicateName]);

    const handleUpdateFieldValue = useCallback((title: string, context: string, create?: boolean) => {
        if (!create) {
            const lookUpCustomField = customFields.find(({ name }) => name.toLowerCase() === title.toLowerCase());

            if (!lookUpCustomField) {
                setCustomFields(prev => [...prev, ({ name: title, additionalContextQuery: context, type: FieldKind.Text } as CustomFieldType)]);
            }  else {
                setCustomFields(prev => (prev.map(custom => custom.name.toLowerCase() === title.toLowerCase()
                    ? ({ ...custom, additionalContextQuery: context, type: custom.type ?? FieldKind.Text }) : custom) as CustomFieldType[]));
            }
        }
        setName(title);
        setAdditionalContextQuery(context);
    // eslint-disable-next-line
    }, [customFields]);

    // eslint-disable-next-line
    const handleEditFieldName = useCallback((oldTitle: string, newTitle: string, create?: boolean) => {
        if (!create) {
            setCustomFields(prev => (prev.map(custom => custom.name.toLowerCase() === oldTitle.toLowerCase()
                ? ({ ...custom, name: newTitle.trim() }) : custom) as CustomFieldType[]));
            setName(newTitle.trim());

            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let hasRenamedCustomField = false;

                const updatedDashboard = { ...dashboard,
                    customFields: dashboard.customFields?.map(custom => {
                        if (custom.name.toLowerCase() === oldTitle.toLowerCase()) {
                            hasRenamedCustomField = true;
                            return ({ ...custom, name: newTitle.trim() });
                        }

                        return custom;
                    }) ?? [] };

                if (hasRenamedCustomField)
                    updatedDashboards.push(updatedDashboard);
            });

            if (!!updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        } else {
           handleUpdateFieldValue(newTitle.trim(), '');
        }
    }, [setCustomFields, dashboards, setDashboards, handleUpdateFieldValue]);

    const handleUpdateContext = useCallback(() => {
        if (filterType && name.trim().toLowerCase() !== filterType.toLowerCase()) {
            // Name changed — rename field and update context in one operation
            setCustomFields(prev => (prev.map(custom => custom.name.toLowerCase() === filterType.toLowerCase()
                ? ({ ...custom, name: name.trim(), additionalContextQuery: additionalContextQuery || '', type: custom.type ?? FieldKind.Text }) : custom) as CustomFieldType[]));

            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let hasRenamedCustomField = false;
                const updatedDashboard = { ...dashboard,
                    customFields: dashboard.customFields?.map(custom => {
                        if (custom.name.toLowerCase() === filterType.toLowerCase()) {
                            hasRenamedCustomField = true;
                            return ({ ...custom, name: name.trim() });
                        }
                        return custom;
                    }) ?? [] };
                if (hasRenamedCustomField)
                    updatedDashboards.push(updatedDashboard);
            });
            if (!!updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        } else {
            handleUpdateFieldValue(name, additionalContextQuery || '');
        }
        onClose();
    }, [name, additionalContextQuery, filterType, setCustomFields, dashboards, setDashboards, handleUpdateFieldValue, onClose]);

    const handleDeleteField = useCallback((title: string) => {
        setCustomFields(prev => prev.filter(custom => custom.name.toLowerCase() !== title.toLowerCase()));
        onClose();
    }, [onClose, setCustomFields]);

    useEffect(() => {
        if (settingsLoaded) {
            if (!!filterType) {
                if (!!customFields.length) {
                    const existingField = customFields.find(custom => custom?.name === filterType);
                    if (existingField) {
                        setName(existingField.name);
                        setAdditionalContextQuery(existingField.additionalContextQuery || '');
                    }
                }
            } else {
                handleUpdateFieldValue(initialName || '', '', true);
                if (initialName) setName(initialName);
            }
        }
    // eslint-disable-next-line
    }, [settingsLoaded]);

    return (<>
        <Dialog open={open} onClose={onClose} className={classes.dialog}>
            <DialogTitle className={classes.dialogTitle}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <TagIcon className={classes.icon} />
                <Typography className={classes.title}>{'Custom fields'}</Typography>
            </Stack>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
            <Stack spacing={3.5}>
                <Stack spacing={1.25} py={2}>
                    <Typography className={classes.sectionLabel}>{'Field name'}</Typography>
                    <Stack spacing={3} width="100%" height="100%">
                        <Stack direction="row" alignItems="center" justifyContent="space-between" pr="12px" width="100%" minHeight="32px">
                            <TextField className={classes.nameField}
                                onChange={(e) => setName(e.target.value)}
                                error={isDuplicateName}
                                helperText={isDuplicateName ? 'A field or tag group with this name already exists' : ''}
                                value={name} fullWidth />
                        </Stack>
                        <Stack direction="column">
                            <Typography className={classes.sectionLabel}>
                                {'Field description'}
                            </Typography>
                            <Typography className={classes.hint}>
                                {'Enhance accuracy by providing additional context on how these should get assigned.'}
                            </Typography>
                            <TextField className={classes.descField}
                                defaultValue={additionalContextQuery}
                                placeholder={'Enter additional context here...'}
                                onChange={(e) => setAdditionalContextQuery(e.target.value)}
                                multiline fullWidth rows={4} />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button className={classes.deleteFieldButton}
                    onClick={() => handleDeleteField(name)}
                    startIcon={<DeleteIcon />}
                    disabled={isSaveDisabled} disableRipple>
                    {'Delete field'}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                    <Button variant="outlined"
                        className={classes.backButton}
                        onClick={onClose}
                        disableRipple>
                        {'Back'}
                    </Button>
                    <Button variant="contained"
                        className={`${classes.saveButton} ${!isSaveDisabled ? classes.saveButtonActive : ''}`}
                        onClick={handleUpdateContext}
                        disabled={isSaveDisabled}
                        disableRipple>
                        {'Save'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    </>);
};

export default ManageCustomFieldsNewModal;